import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { CommonMaterialModule } from '@nrwl/common-material';
import { FusionAuthenticationModule } from '@nrwl/fusion-authentication';
import { CommonLibModule, CountryListService, DialogService } from '@nrwl/common-lib';
import { ViewTableModule } from '@nrwl/view-table';

import { WizardLauncherService } from './wizard/wizard.launcher.service';
import { BaseWizardService } from './wizard/services/base.wizard.service';
import { SitesWizardService } from './wizard/services/sites/sites.wizard.service';
import { WizardControlsModule } from './wizard/controls/wizard.controls.module';
import { UserWizardService } from './wizard/services/user/user.wizard.service';
import { UserUpdateComponent } from './wizard/flows/user/update.user/user.update.component';
import { UserAddLicenseComponent } from './wizard/flows/user/agent.info/user.add.license.component';
import { UserToGroupComponent } from './wizard/flows/user/add.user.to.group/user.to.group.component';
import { UserToRoleComponent } from './wizard/flows/user/add.users.to.roles/user.to.role.component';
import { UserGroupsComponent } from './wizard/flows/user/invite.user/user.groups.component';
import { UserInviteRolesComponent } from './wizard/flows/user/invite.user/user.invite.roles.component';
import { UserInviteComponent } from './wizard/flows/user/invite.user/user.invite.component';
import { UsersGroupComponent } from './wizard/flows/user/new.user.group/users.group.component';
import { UsersRolesComponent } from './wizard/flows/user/new.user.group/users.roles.component';
import { UsersSubContentComponent } from './wizard/flows/user/new.user.group/users.sub.content.component';
import { UserFromGroupComponent } from './wizard/flows/user/remove.user.from.group/user.from.group.component';
import { UserFromRoleComponent } from './wizard/flows/user/remove.users.from.roles/user.from.role.component';
import { WizardRouteStarterComponent } from './wizard/wizard.route.starter.component';
import { UserUpdateWizardComponent } from './wizard/flows/user/update.user/user.update.wizard';
import { UserAddWizardComponent } from './wizard/flows/user/add.user.to.group/user.add.wizard.component';
import { UserRemoveWizardComponent } from './wizard/flows/user/remove.user.from.group/user.remove.wizard.component';
import { NewSiteWizardComponent } from './wizard/flows/sites/new.site.wizard';
import { SitesDescHtmlComponent } from './wizard/flows/sites/sites.desc.page/sites.desc.component';
import { SitesLocationHtmlComponent } from './wizard/flows/sites/sites.location.page/sites.location.component';
import { SitesCreateHtmlComponent } from './wizard/flows/sites/sites.review.page/sites.create.component';
import { SitesSubnetHtmlComponent } from './wizard/flows/sites/sites.subnets.page/sites.subnet.component';
import { SitesCreateResultsHtmlComponent } from './wizard/flows/sites/sites.final.page/sites.create.results.component';
import { UpdateUserPasswordWizardComponent } from './wizard/flows/user/update.user.password/update.user.password';
import { UpdateUserPasswordComponent } from './wizard/flows/user/update.user.password/update.user.password.component';
import { SitesReviewSubContentCommponent } from './wizard/flows/sites/sites.review.page/review.sub.content.component';
import { NewUserGroupWizardComponent } from './wizard/flows/user/new.user.group/new.user.group.wizard';
import { AddUserWizardComponent } from './wizard/flows/user/add.user/add.user.wizard.component';
import { AddUserComponent } from './wizard/flows/user/add.user/add.user.component';
import { NewApplicationWizardComponent } from './wizard/flows/application/new.application.wizard';
import { ApplicationWizardService } from './wizard/services/application/application.wizard.service';
import { ActiveTestHtmlComponent } from './wizard/flows/application/app.active.test.page/active.test.component';
import { ApplicationBasicHtmlComponent } from './wizard/flows/application/app.basic.page/application.basic.component';
import { CreateResultsHtmlComponent } from './wizard/flows/application/app.final.page/create.results.component';
import { ReviewHtmlComponent } from './wizard/flows/application/app.review.page/review.component';
import { ApplicationIdentityComponent } from './wizard/flows/application/app.basic.page/application.identity.component';
import { NetworkPortInputComponent } from './wizard/flows/application/controls/network.port.input/network.port.input.component';
import { HostInputComponent } from './wizard/flows/application/controls/host.input/host.input.component';
import { HostsInputComponent } from './wizard/flows/application/controls/hosts.input/hosts.input.component';
import { ProtocolInputComponent } from './wizard/flows/application/controls/protocol.input/protocol.input.component';
import { AddUserToRolesWizard } from './wizard/flows/user/add.users.to.roles/add.user.to.roles.wizard';
import { RemoveUserFromRolesWizard } from './wizard/flows/user/remove.users.from.roles/remove.user.from.roles.wizard';
import { IPValidator } from './wizard/directives/ip.validator.directive';
import { DatasourceWizardComponent } from './wizard/flows/datasource/datasource.wizard';
import { DatasourceDefinitionComponent } from './wizard/flows/datasource/datasource.definition.page/datasource.definition.component';
import { DatasourceSiteSelectionComponent } from './wizard/flows/datasource/datasource.site.selection.page/datasource.site.selection.component';
import { DatasourceReviewComponent } from './wizard/flows/datasource/datasource.review.page/datasource.review.component';
import { DatasourceCreateResultsComponent } from './wizard/flows/datasource/datasource.create.results.page/datasource.create.results.component';
import { DatasourceWizardService } from './wizard/services/datasource/datasource.wizard.service';
import { DatasourceAgentProxyComponent } from './wizard/flows/datasource/datasource.agent.proxy.page/datasource.agent.proxy.component';
import { DatasourceAgentNetworkComponent } from './wizard/flows/datasource/datasource.agent.network.page/datasource.agent.network.component';
import { DatasourceAgentDescriptionComponent } from './wizard/flows/datasource/datasource.agent.desc.page/datasource.agent.desc.component';
import { DatasourceGigaTestReviewComponent } from './wizard/flows/datasource/datasource.gigatest.review.page/datasource.gigatest.review.component';
import { ThresholdWizardService } from './wizard/services/threshold/threshold.wizard.service';
import { UserResultsComponent } from './wizard/flows/user/user-results/user-results.component';
import { UpdateUserPasswordResultsComponent } from './wizard/flows/user/update-user-password-results/update-user-password-results.component';
import { NewUserGroupResultComponent } from './wizard/flows/user/new.user.group/new.user.group.result/new.user.group.result.component';
import { DisableControlDirectiveGeneric } from './wizard/directives/disable.control.directive';
import { UserEmailNotificationComponent } from './wizard/flows/user/user.email.notification/user.email.notification.component';
import { UserEmailNotificationWizard } from './wizard/flows/user/user.email.notification/user.email.notification';
import { UserSiteSelectionWizard } from './wizard/flows/user/user.sites.selection.component/user.site.selection.wizard';
import { UserSitesSelectionComponent } from './wizard/flows/user/user.sites.selection.component/user.sites.selection.component';
import { IPAMConfigurationWizardComponent } from './wizard/flows/ipam/ipam.configuration.wizard';
import { IPAMConfigurationComponent } from './wizard/flows/ipam/ipam.configuration/ipam.configuration.component';
import { IPAMConfigWizardService } from './wizard/services/ipam/ipam.configuration.wizard.service';
import { IPAMResultsComponent } from './wizard/flows/ipam/ipam.results/ipam.results.component';
import { SyslogConfigurationWizardComponent } from './wizard/flows/syslog/syslog.configuration.wizard';
import { SyslogConfigurationComponent } from './wizard/flows/syslog/syslog.configuration/syslog.configuration.component';
import { SyslogResultsComponent } from './wizard/flows/syslog/syslog.results/syslog.results.component';
import { SyslogConfigWizardService } from './wizard/services/syslog/syslog.configuration.service';
import { ActiveTestTypesComponent } from './wizard/flows/active.test/active.test.types.page/active.test.types.component';
import { ActiveTestWizardComponent } from './wizard/flows/active.test/active.test.wizard.component';
import { ActiveTestWizardService } from './wizard/services/active.test/active.test.wizard.service';
import { TransactionThresholdComponent } from './wizard/flows/ata-threshold/transaction.threshold/transaction.threshold.component';
import { CustomAtaThresholdWizardComponent } from './wizard/flows/ata-threshold/custom.ata.threshold.wizard';
import { ThresholdAppsSitesSelectionComponent } from './wizard/flows/ata-threshold/threshold.apps.sites.selection/threshold.apps.sites.selection.component';
import { SetUpThresholdComponent } from './wizard/flows/ata-threshold/set-up-threshold/set-up-threshold.component';
import { ReviewThresholdComponent } from './wizard/flows/ata-threshold/review-threshold/review-threshold.component';
import { MlSelectApplicationWizardComponent } from './wizard/flows/machine.learning/ml-select-application-wizard/ml-select-application-wizard.component';
import { MlSelectApplicationComponent } from './wizard/flows/machine.learning/ml-select-application-wizard/ml-select-application/ml-select-application.component';
import { MlSelectSiteWizardComponent } from './wizard/flows/machine.learning/ml-select-site-wizard/ml-select-site-wizard.component';
import { MlSelectSiteComponent } from './wizard/flows/machine.learning/ml-select-site-wizard/ml-select-site/ml-select-site.component';
import { MlSelectKpiWizardComponent } from './wizard/flows/machine.learning/ml-select-kpi-wizard/ml-select-kpi-wizard.component';
import { MlSelectKpiComponent } from './wizard/flows/machine.learning/ml-select-kpi-wizard/ml-select-kpi/ml-select-kpi.component';
import { AtaThresholdWizardService } from './wizard/services/ata-threshold/ata.threshold.wizard.service';
import { ThresholdResultPageComponent } from './wizard/flows/ata-threshold/threshold-result-page/threshold-result-page.component';
import { ActiveTestLicenseButtonComponent } from './wizard/flows/active.test/active.test.license.button/active.test.license.button.component';
import { UpdateUserResultsComponent } from './wizard/flows/user/update.user/user.update.results/user.update.results.component';
import { ThresholdSettingsModule } from '../../../threshold-settings/src';
import { ThresholdWizardComponent } from './wizard/flows/threshold/threshold.wizard';
import { ThresholdSiteSelectionComponent } from './wizard/flows/threshold/threshold.site.selection.page/threshold.site.selection.page.component';
import { ThresholdApplicationSelectionComponent } from './wizard/flows/threshold/threshold.application.selection.page/threshold.application.selection.page.component';
import { ThresholdHistoricalSettingsPageComponent } from './wizard/flows/threshold/threshold.historical.settings.page/threshold.historical.settings.page.component';
import { ThresholdServerSelectionPageComponent } from './wizard/flows/threshold/threshold.server.selection.page/threshold.server.selection.page.component';
import { ThresholdSettingsComponent } from './wizard/flows/threshold/threshold.settings.page/threshold.settings.page.component';
import { AddUserFusionComponent } from './wizard/flows/user/add.user/add.user.fusion.component';
import { AddUserFusionSsoComponent } from './wizard/flows/user/add.user/add.user.fusion.sso.component';
import { FusionSsoUserUpdateComponent } from './wizard/flows/user/update.user/update.user.fusion.sso.component';
import { FusionSsoUserRemoveWizardComponent } from './wizard/flows/user/fusion.remove.user.from.group/fusion.user.remove.wizard.component';
import { FusionSsoUserFromGroupComponent } from './wizard/flows/user/fusion.remove.user.from.group/fusion.sso.user.from.group.component';
import { FusionSsoUserAddWizardComponent } from './wizard/flows/user/fusion.sso.add.user.to.group/fusion.sso.user.add.wizard.component';
import { FusionSsoUserToGroupComponent } from './wizard/flows/user/fusion.sso.add.user.to.group/fusion.sso.user.to.group.component';
import { LdapServerConfigurationComponent } from './wizard/flows/ldap-servers/ldap.server.configuration/ldap.server.configuration.component';
import { LdapServerResultComponent } from './wizard/flows/ldap-servers/ldap.server.result/ldap.server.result.component';
import { LdapServerWizardComponent } from './wizard/flows/ldap-servers/ldap.server.wizard.component';
import { RadiusServerConfigurationComponent } from './wizard/flows/radius-servers/radius.server.configuration/radius.server.configuration.component';
import { RadiusServerWizardComponent } from './wizard/flows/radius-servers/radius.server.wizard.component';
import { RadiusResultsComponent } from './wizard/flows/radius-servers/radius.server.results/radius.servers.results.component';
import { DatasourceAgentUpgradeComponent } from './wizard/flows/datasource/datasource.agent.upgrade.page/datasource.agent.upgrade';
import { DatasourceAgentUpgradeResultComponent } from './wizard/flows/datasource/datasource.agent.upgrade.result.page/datasource.agent.upgrade.result.component';

@NgModule({
  imports: [
    CommonModule,
    CommonMaterialModule,
    FusionAuthenticationModule,
    CommonLibModule,
    WizardControlsModule,
    ThresholdSettingsModule,
    FormsModule,
    ReactiveFormsModule,
    ViewTableModule
  ],
  declarations: [
    WizardRouteStarterComponent,
    NewSiteWizardComponent,
    UserUpdateWizardComponent,
    UserAddWizardComponent,
    UserRemoveWizardComponent,
    SitesDescHtmlComponent,
    SitesLocationHtmlComponent,
    SitesCreateHtmlComponent,
    SitesSubnetHtmlComponent,
    SitesReviewSubContentCommponent,
    SitesCreateResultsHtmlComponent,
    UserUpdateComponent,
    UserAddLicenseComponent,
    UserToGroupComponent,
    UserToRoleComponent,
    UserGroupsComponent,
    UserInviteRolesComponent,
    UserInviteComponent,
    UsersGroupComponent,
    UsersRolesComponent,
    UsersSubContentComponent,
    UserFromGroupComponent,
    UserFromRoleComponent,
    UserEmailNotificationComponent,
    UserEmailNotificationWizard,
    UserSiteSelectionWizard,
    UserSitesSelectionComponent,
    UpdateUserPasswordWizardComponent,
    UpdateUserPasswordComponent,
    NewUserGroupWizardComponent,
    AddUserWizardComponent,
    AddUserComponent,
    NewApplicationWizardComponent,
    ActiveTestHtmlComponent,
    ApplicationBasicHtmlComponent,
    CreateResultsHtmlComponent,
    ReviewHtmlComponent,
    ApplicationIdentityComponent,
    NetworkPortInputComponent,
    HostInputComponent,
    HostsInputComponent,
    ProtocolInputComponent,
    AddUserToRolesWizard,
    RemoveUserFromRolesWizard,
    IPValidator,
    DisableControlDirectiveGeneric,
    DatasourceWizardComponent,
    DatasourceDefinitionComponent,
    DatasourceSiteSelectionComponent,
    DatasourceReviewComponent,
    DatasourceCreateResultsComponent,
    DatasourceAgentProxyComponent,
    DatasourceAgentNetworkComponent,
    DatasourceAgentDescriptionComponent,
    DatasourceGigaTestReviewComponent,
    DatasourceAgentUpgradeComponent,
    DatasourceAgentUpgradeResultComponent,
    UserResultsComponent,
    UpdateUserPasswordResultsComponent,
    NewUserGroupResultComponent,
    IPAMConfigurationWizardComponent,
    IPAMConfigurationComponent,
    IPAMResultsComponent,
    SyslogConfigurationWizardComponent,
    SyslogConfigurationComponent,
    SyslogResultsComponent,
    ActiveTestTypesComponent,
    ActiveTestWizardComponent,
    ActiveTestLicenseButtonComponent,
    UpdateUserResultsComponent,
    TransactionThresholdComponent,
    CustomAtaThresholdWizardComponent,
    ThresholdAppsSitesSelectionComponent,
    SetUpThresholdComponent,
    ReviewThresholdComponent,
    MlSelectApplicationWizardComponent,
    MlSelectApplicationComponent,
    MlSelectSiteWizardComponent,
    MlSelectSiteComponent,
    MlSelectKpiWizardComponent,
    MlSelectKpiComponent,
    ThresholdResultPageComponent,
    ThresholdSiteSelectionComponent,
    ThresholdApplicationSelectionComponent,
    ThresholdHistoricalSettingsPageComponent,
    ThresholdServerSelectionPageComponent,
    ThresholdSettingsComponent,
    ThresholdWizardComponent,
    AddUserFusionComponent,
    AddUserFusionSsoComponent,
    FusionSsoUserUpdateComponent,
    FusionSsoUserRemoveWizardComponent,
    FusionSsoUserFromGroupComponent,
    FusionSsoUserAddWizardComponent,
    FusionSsoUserToGroupComponent,
    LdapServerConfigurationComponent,
    LdapServerResultComponent,
    LdapServerWizardComponent,
    RadiusServerConfigurationComponent,
    RadiusServerWizardComponent,
    RadiusResultsComponent
  ],
  exports: [WizardRouteStarterComponent],
  providers: [
    WizardLauncherService,
    BaseWizardService,
    SitesWizardService,
    UserWizardService,
    CountryListService,
    DialogService,
    ApplicationWizardService,
    DatasourceWizardService,
    ThresholdWizardService,
    IPAMConfigWizardService,
    SyslogConfigWizardService,
    ActiveTestWizardService,
    AtaThresholdWizardService
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class WizardModule {}
